import Vue from "vue"
import { extend, setInteractionMode, ValidationObserver, ValidationProvider } from "vee-validate/dist/vee-validate.full"
import { size } from "vee-validate/dist/rules"
import pL from "js-regex-pl"

setInteractionMode("passive")

let validateImage = function (file, minDimensions, maxDimensions) {
  let URL = window.URL || window.webkitURL

  return new Promise(function (resolve) {
    let image = new Image()

    image.onerror = function () {
      return resolve(false)
    }

    image.onload = function () {
      let valid = true

      if (minDimensions) {
        if (minDimensions.width && this.width < minDimensions.width) {
          valid = false
        }

        if (minDimensions.height && this.height < minDimensions.height) {
          valid = false
        }
      }

      if (maxDimensions) {
        if (maxDimensions.width && this.width > maxDimensions.width) {
          valid = false
        }

        if (maxDimensions.height && this.height > maxDimensions.height) {
          valid = false
        }
      }

      return resolve(valid)
    }

    image.src = URL.createObjectURL(file)
  })
}

extend("fileLimit", {
  validate(value, args) {
    if (value.length <= parseInt(args.fileLimit)) {
      return true
    }
    return "You have exceeded the maximum number of tracks allowed for your plan."
  },
  params: ["fileLimit"],
  message: "Tracks limit exceeded.",
})

extend("aspectSquare", {
  validate(value) {
    let URL = window.URL || window.webkitURL

    return new Promise(function (resolve) {
      let image = new Image()

      image.onerror = function () {
        return resolve(false)
      }

      image.onload = function () {
        let valid = true

        if (this.width !== this.height) {
          valid = false
        }

        return resolve(valid)
      }

      image.src = URL.createObjectURL(value[0])
    })
  },
  params: ["aspectSquare"],
  message: "Image must have equal width and height.",
})

extend("dimensions", {
  validate(value, args) {
    let minDimensions = {
      width: null,
      height: null,
    }

    let min = args.min.split("x")

    minDimensions = {
      width: min[0],
      height: min[1],
    }

    let maxDimensions

    if (args.max) {
      maxDimensions = {
        width: null,
        height: null,
      }

      let max = args.max.split("x")

      maxDimensions = {
        width: max[0],
        height: max[1],
      }
    }

    return Promise.resolve(validateImage(value[0], minDimensions, maxDimensions))
      .then((valid) => {
        return valid
      })
      .catch(() => {
        return false
      })
  },
  params: ["min", "max"],
  message: "Invalid image size.",
})

extend("url", {
  validate: (str) => {
    let pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
      "i"
    ) // fragment locator
    return !!pattern.test(str)
  },
  message: "The {_field_} field must be a valid URL",
})

extend("pretty_url", {
  validate: (value) => {
    let strongRegex = new RegExp("^([A-Za-z0-9\\-\\_]+)$")
    return strongRegex.test(value)
  },
  message: "The {_field_} should be at least 6 characters. Only letters, numbers, dashes and underscores are allowed.",
})

extend("welcome_prompt_size", {
  ...size,
  message: "The {_field_} field size must be less than 10MB",
})

extend("station_name", {
  validate: (value) => {
    let strongRegex = new RegExp(`^([${pL}0-9\\.\\-\\_\\(\\) ]+)$`)
    return strongRegex.test(value)
  },
  message:
    "The station name should be at least 6 characters. Only letters, numbers, spaces, dashes, dots and underscores are allowed.",
})

extend("zone_offset", {
  validate: (value) => {
    if (!value) return true
    const zoneOffsetPattern = /^[+-]\d{2}:\d{2}$/
    return zoneOffsetPattern.test(value)
  },
  message: 'Zone offset must be in format "+HH:mm" or "-HH:mm" (e.g., "+05:00" or "-03:00")',
})

extend("multiselect", {
  validate: (value) => {
    if (!value.path) return false
    return true
  },
  message: "This field is required",
})

extend("relay_url", {
  validate(value) {
    const regex = /^(https?:\/\/|rtmp:\/\/)[^\s/$.?#].[^\s]*$/i;
    return regex.test(value);
  },
  message: "The {_field_} field must be a valid HTTP(S) or RTMP URL"
});

extend("stream_url", {
  validate(value) {
    if (!value) return true;
    const regex = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
    return regex.test(value);
  },
  message: "The {_field_} field must be a valid HTTP or HTTPS URL"
});

extend("password_has_digit", {
  validate: (value) => /\d/.test(value),
  message: "The {_field_} must contain at least one digit",
})

extend("password_has_special", {
  validate: (value) => /[^A-Za-z0-9]/.test(value),
  message: "The {_field_} must contain at least one special character",
})

export const resolveJsonError = (value) => {
  try {
    JSON.parse(value)
    return null
  } catch (e) {
    const posMatch = e.message.match(/at position (\d+)/)
    if (posMatch) {
      const pos = parseInt(posMatch[1], 10)
      const line = value.slice(0, pos).split("\n").length
      return `Invalid JSON at line ${line}: ${e.message}`
    }
    return `Invalid JSON: ${e.message}`
  }
}

extend("json", {
  validate: (value) => {
    const error = resolveJsonError(value)
    if (!error) return true
    return { valid: false, data: { jsonError: error } }
  },
  message: (_, { jsonError }) => jsonError,
})

extend("max_array_item_length", {
  validate: (value, { length }) => {
    if (!Array.isArray(value)) return true
    return value.every((item) => String(item).length <= Number(length))
  },
  params: ["length"],
  message: "Each {_field_} entry must be {length} characters or fewer",
})

extend("password_not_email", {
  params: ["email"],
  validate: (value, { email }) => {
    if (!email) return true
    return value.toLowerCase() !== email.toLowerCase()
  },
  message: "The {_field_} must not be the same as your email",
})

Vue.component("ValidationObserver", ValidationObserver)
Vue.component("ValidationProvider", ValidationProvider)
